import {
  ApiRequest,
  OfficialPublicStatus,
  OfficialApprovalStatus
} from "@/api/types";

import { PartiallyPartial } from "@/helpers/type-helper";

/** ================================================================================
 * 編集フォーム関連
 ================================================================================ */
// 必ずフォームに表示するofficial_data
export const defaultOfficialDataList = [
  {
    name: "representative",
    label: "代表者",
    type: "input",
    required: true
  },
  {
    name: "location",
    label: "所在地",
    type: "input",
    required: true
  },
  {
    name: "capital",
    label: "資本金",
    type: "input",
    required: false
  },
  {
    name: "established_at",
    label: "設立日",
    type: "date",
    required: true
  },
  {
    name: "number_of_employees",
    label: "従業員数",
    type: "input",
    required: true
  }
];

export const formConfig = {};

// フォームの項目名称
export const formKeyLabelList = {
  charms: {
    section_name: {
      label: "企業の魅力",
      help: "",
      placeholder: ""
    },
    image1: {
      label: "画像",
      help: "430x280px（860x560px）＊jpg推奨",
      placeholder: ""
    },
    title: { label: "項目名", help: "", placeholder: "" },
    description1: { label: "内容", help: "", placeholder: "" }
  },
  images: {
    section_name: { label: "イメージギャラリー", help: "", placeholder: "" },
    image1: {
      label: "画像",
      help: "430x280px（860x560px）＊jpg推奨",
      placeholder: ""
    },
    title: { label: "タイトル", help: "", placeholder: "" },
    description1: { label: "説明文", help: "", placeholder: "" }
  },
  movies: {
    section_name: { label: "オリジナルムービー", help: "", placeholder: "" },
    movie1: {
      label: "動画URL",
      help:
        "埋込URLは以下の手順で取得できます。YouTube上の動画>>共有>>埋め込む>>コピー",
      placeholder: ""
    },
    title: { label: "動画タイトル", help: "", placeholder: "" },
    description1: { label: "説明文", help: "", placeholder: "" }
  },
  official_data: { label: "企業情報", help: "", placeholder: "" },
  contract_id: { label: "契約", help: "", placeholder: "" },
  official_description: {
    label: "説明文",
    help:
      "200〜400文字程度で会社概要を紹介するメッセージを入力してください。URLを記載することはできません。",
    placeholder: ""
  },
  official_image: {
    label: "メイン画像",
    help:
      "ページの一番上に表示される横長の画像です。以下の要件を満たす画像を設定してください。1120x300px または 2240x600px・jpg推奨",
    placeholder: ""
  },
  top_head_image: {
    label: "注目の企業画像",
    help:
      "“注目の企業”ページに表示される画像です。以下の要件を満たす画像を設定してください。246x156px",
    placeholder: ""
  },
  start_at: { label: "更新希望日時", help: "", placeholder: "" }
};

export type CharmsKeyLabelTypes = keyof typeof formKeyLabelList.charms;
export type ImagesKeyLabelTypes = keyof typeof formKeyLabelList.images;
export type MoviesKeyLabelTypes = keyof typeof formKeyLabelList.movies;

export const requiredOfficialDataLabelList = defaultOfficialDataList
  .filter(v => v.required)
  .map(v => v.label);

// 必須なofficial_dataかどうか
export const isRequiredOfficialData = (label: string) => {
  return requiredOfficialDataLabelList.includes(label);
};

// 「企業情報」のタイプ（official_dataに含まれるものを除く）
export const basicInfoFormContentNameList = [
  "contract_id",
  "official_description",
  "official_image",
  "top_head_image"
] as const;
export type BasicInfoFormContentNameType = typeof basicInfoFormContentNameList[number];

// official_contentsのタイプ（「企業の魅力」「イメージギャラリー」「オリジナルムービー」）
export const officialContentNameList = ["charms", "images", "movies"] as const;
export type OfficialContentsNameType = typeof officialContentNameList[number];

// 掲載依頼関連データの型
type OfficialSubmitType = {
  comment?: string;
  submit_type?: PreferredDateType;
  start_at?: string;
};
export interface EditingOfficialData
  extends PartiallyPartial<ApiRequest.OfficialData, "sort_order"> {
  tmpId?: number;
  required?: boolean;
}

// 編集フォーム - 「企業情報」の型
type BasicInfoFormContentFormType = {
  contract_id?: number;
  official_data?: EditingOfficialData[];
  official_description?: string;
  official_image?: string;
  top_head_image?: string;
};

export interface EditingOfficialContent extends ApiRequest.OfficialContent {
  tmpId?: number;
  uncroppedImage?: string;
}

// 編集フォーム - official_contents（「企業の魅力」「イメージギャラリー」「オリジナルムービー」）の型
type OfficialContentFormType = Partial<
  Record<OfficialContentsNameType, EditingOfficialContent[]>
>;

// 公開/更新日時の型
export type PreferredDateType = "sameDate" | "specifiedDate";

// 編集フォームの型
export type OfficialEditingFormType = BasicInfoFormContentFormType &
  OfficialContentFormType &
  OfficialSubmitType;

// official_contentsで値更新できる項目
export const editableOfficialContentKeyList = [
  "title",
  "description1",
  "image1",
  "movie1"
] as const;
export type EditableOfficialContentKeyType = typeof editableOfficialContentKeyList[number];

export const YOUTUBE_EMBED_HTML_PATTERN = /<iframe.*<\/iframe>/;

export const YOUTUBE_URL_PATTERN = /^(?:http(?:s)?:\/\/)?(?:www\.)?(?:m\.)?(?:youtu\.be\/|youtube\.com\/(?:(?:watch)?\?(?:.*&)?v(?:i)?=|(?:embed|v|vi|user)\/))([^?&"'>]+)(&t=.*)?$/;

/** ================================================================================
 * エラー関連
 ================================================================================ */
// エラーの型
export type OfficialError = {
  key: {
    mainKey: keyof OfficialEditingFormType;
    id?: number;
    subKey?: EditableOfficialContentKeyType | "text" | "label";
  };
  message: string;
};

export type OfficialWarn = {
  key: {
    mainKey: keyof OfficialEditingFormType;
    id?: number;
    subKey?: EditableOfficialContentKeyType | "text" | "label";
  };
  message: string;
};

const forbiddenUrlPattern = new RegExp(
  [
    "https://mypage", // 競合他社（i-web）
    "https://www.wantedly.com", // 競合他社（Wantedly）
    "https://job.axol.jp", // 競合他社（マイナビAOL）
    "https://www.goodfind.jp", // 競合他社（Goodfind）
    "https://job.mynavi.jp", // 競合他社（マイナビ）
    "https://job.rikunabi.com", // 競合他社（リクナビAOL）
    "https://communicator.gakujo.ne.jp", // LINE QRコード（あさがくナビ）
    "https://user.digmee-connect.jp", // LINE QRコード（HR PRIME）
    "https://www.youtube.com", // SNS（YouTube）
    "https://www.instagram.com" // SNS（Instagram）
  ]
    .map(pattern => {
      const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return `.*${escapedPattern}.*`;
    })
    .join("|"),
  "i"
);

export const hasForbiddenUrl = (value?: string): boolean => {
  if (!value) return false;
  return forbiddenUrlPattern.test(value);
};

/**
 * hr, career, recruit を含むURLはワーニング
 */
const warnUrlPattern = /https*:\/\/[-_~\/\.<>%:\?#/@!\$&'()\*\+,;=a-zA-Z0-9]*(hr|career|recruit)[-_~\/\.<>%:\?#/@!\$&'()\*\+,;=a-zA-Z0-9]*/;

export const hasWarnUrl = (value?: string): boolean => {
  if (!value) return false;
  return warnUrlPattern.test(value);
};

export const warnUrlMessage =
  "採用サイトの可能性があるURLが含まれています。掲載できるのはコーポレートサイトのトップページのみです。問題ない場合そのまま掲載依頼してください。";

/** ================================================================================
 * 画面表示
 ================================================================================ */
// 公開ステータス
export const publicStatusMapping: {
  [key in OfficialPublicStatus]: string;
} = {
  private: "非公開",
  coming_soon: "公開予定",
  suspend: "一時公開停止",
  public: "公開中",
  ended: "公開終了"
} as const;

/**
 * 表示する公開ステータスを返却
 * @param status ステータス
 * @returns string ステータス文言
 */
export const getDisplayPublicStatus = (
  status: OfficialPublicStatus
): string => {
  return publicStatusMapping[status];
};

// 承認ステータス
export const approvalStatusMapping: {
  [key in OfficialApprovalStatus]: string;
} = {
  draft: "下書き",
  pending: "承認待ち",
  approved: "承認済み",
  rejected: "棄却"
} as const;

/**
 * 表示する承認ステータスを返却
 * @param status ステータス
 * @returns string ステータス文言
 */
export const getApprovalPublicStatus = (
  status: OfficialApprovalStatus
): string => {
  return approvalStatusMapping[status];
};

// フォームのplaceholder
export const formPlaceholderList = {
  description:
    "例）【データをテクノロジーによって可視化し、透明性のある社会を創る】\n\n\
  多くの人にとって人生で最も時間を投じる対象は「仕事」。にも関わらず、就職活動では意思決定の基準となるようなデータにアクセスしづらく、HR市場は半世紀以上もの間、勘と経験によって成り立ってきました。\n\
  ワンキャリアは、そういった課題をテクノロジーとデータの力で解消することにより、仕事選びの概念と人材採用を変え、採用におけるミスマッチを解消し、世の中の変遷を妨げる障壁がゼロになる世界を目指しています。",
  charm: {
    title:
      "タイトルを入力してください。\n\n\
    例）人生で最も重要な意思決定である、仕事選びに寄り添いたい",
    content:
      "150〜300文字程度で貴社の魅力を紹介するメッセージを入力してください。\n\n\
    例）多くの人にとって人生で最も時間を投じる対象は「仕事」。にも関わらず、就職活動では意思決定の基準となるようなデータにアクセスしづらく、HR市場は半世紀以上もの間、勘と経験によって成り立ってきました。\n\
    生産年齢人口が減少し続け、過去30年間ずっと停滞感のあった日本では、今後更に大きなゲームチェンジに直面します。また、人口ボーナスが終わった日本で企業が成長を続けるためには「従業員一人一人の才能を活性化させる（生産性を上げる）」ことが必須となります。日本をRevitalizeするために、HRというマーケットが重要になるのは間違いありません。\n\
    我々はその中で最もHRに関するデータを集める会社となり、人が最も時間を投じる「仕事」の概念を変えたいと思います。"
  },
  image_gallery: {
    title: "画像のタイトルを入力してください",
    content: "画像の説明文を入力してください"
  },
  movie: {
    title: "動画のタイトルを入力してください",
    url: "YouTubeのURLを入力してください",
    content: "動画の内容を説明するメッセージを入力してください"
  }
};

export const formDescriptionList = {
  charm:
    "テキスト+画像で貴社の魅⼒を伝えることができます。\n\
    学⽣によく⾒られているページ内コンテンツです。\n\n\
    企業の戦略や主力商品の説明などを入れて、より訴求力を高めることが可能です。",
  image_gallery:
    "画像+説明文（テキスト70文字以内）で貴社の雰囲気を伝えるページです。\n\
    オフィスの風景や働くメンバーの画像などを入れて、より臨場感のある求人にすることができます。",
  movie: "動画で貴社の雰囲気を伝えることができます。"
};
